import { browserLocalPersistence, browserSessionPersistence, createUserWithEmailAndPassword, getRedirectResult, GoogleAuthProvider, OAuthProvider, onAuthStateChanged, sendPasswordResetEmail, setPersistence, signInWithCustomToken, signInWithEmailAndPassword, signInWithPopup, signInWithRedirect, signOut } from 'firebase/auth'
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { auth, db } from '../../firebaseConfig'

declare global {
  interface Window {
    Kakao: {
      init: (appKey: string) => void
      isInitialized: () => boolean
      Auth: {
        login: (options: {
          success: (authObj: any) => void
          fail: (error: any) => void
        }) => void
        authorize: (options: {
          redirectUri: string
          state?: string
          scope?: string
        }) => void
        getAccessToken: () => string | null
        setAccessToken: (token: string) => void
        logout: (callback?: () => void) => void
      }
      API: {
        request: (options: {
          url: string
          success: (response: any) => void
          fail: (error: any) => void
        }) => void
      }
    }
    AppleID: {
      auth: {
        init: (config: {
          clientId: string
          scope: string
          redirectURI: string
          state?: string
          usePopup?: boolean
        }) => void
        signIn: (config?: {
          clientId?: string
          scope?: string
          redirectURI?: string
          state?: string
          usePopup?: boolean
        }) => Promise<{
          authorization: {
            code: string
            id_token: string
            state: string
          }
          user?: {
            email: string
            name: {
              firstName: string
              lastName: string
            }
          }
        }>
      }
    }
    naver_id_login?: any // 네이버 로그인 SDK (글로벌 객체)
  }
}

export const useUserStore = defineStore('user', () => {
  const systemStore = useSystemStore()
  const contractStore = useContractStore()
  const loading = ref(false)
  const getUserDataLoading = ref(false)
  const isSessionInitialized = ref(false)
  const userStoreData = ref()
  const tempSocialUserData = ref() // 회원가입 미완료 소셜 사용자 임시 데이터

  const router = useRouter()
  const documents = ref<any[]>([])
  const document = ref<any>()
  const uploadDocument = ref<any>()
  const contractData = ref<any[]>([]) // 계약 데이터 저장
  const messageData = ref<any[]>([]) // 메시지 데이터 저장
  const campaignData = ref<any[]>([]) // 캠페인 데이터 저장

  // myHome 페이지 전용 병렬 데이터 로딩 함수
  async function loadMyHomeData() {
    try {
      // 사용자 타입에 따라 로딩할 데이터 결정
      const isAdvertiser = userStoreData.value?.type === 'advertiser'
      const promises: Promise<any>[] = []

      // 계약 데이터는 공통으로 로드
      promises.push(
        (async () => {
          return await contractStore.fetchUserContracts()
        })(),
      )

      // 메시지 데이터는 공통으로 로드
      promises.push(
        (async () => {
          const { useMessageStore } = await import('./message')
          const messageStore = useMessageStore()

          // 실시간 리스너 설정
          await messageStore.fetchChats()

          // 기존 데이터가 있으면 즉시 반환, 없으면 짧은 시간만 대기
          if (messageStore.chat && Array.isArray(messageStore.chat) && messageStore.chat.length > 0) {
            return messageStore.chat
          }

          // 최대 300ms만 대기 (기존 1000ms에서 단축)
          return new Promise((resolve) => {
            const timeout = setTimeout(() => {
              resolve(messageStore.chat || [])
            }, 300)

            // 데이터가 로드되면 즉시 반환
            const unwatch = watch(() => messageStore.chat, (newValue) => {
              if (newValue && Array.isArray(newValue) && newValue.length > 0) {
                clearTimeout(timeout)
                unwatch()
                resolve(newValue)
              }
            }, { immediate: true })
          })
        })(),
      )

      // 광고주인 경우 캠페인 데이터도 로드
      if (isAdvertiser) {
        promises.push(
          (async () => {
            const { useCampaignStore } = await import('./campaign')
            const campaignStore = useCampaignStore()
            await campaignStore.getCampaignList()

            // 해당 광고주의 캠페인들만 필터링
            return campaignStore.documents.filter((campaign: any) => {
              const hasAdvertiserId = campaign.advertiserId === userStoreData.value?.uid
              const hasAdId = campaign.adId === userStoreData.value?.uid
              const hasUserId = campaign.userId === userStoreData.value?.uid
              return (hasAdvertiserId || hasAdId || hasUserId) && campaign.step2
            })
          })(),
        )
      }

      // 모든 데이터를 병렬로 로드
      const results = await Promise.allSettled(promises)

      // 계약 데이터 처리
      if (results[0]?.status === 'fulfilled') {
        contractData.value = (results[0] as PromiseFulfilledResult<any[]>).value
      }
      else {
        contractData.value = []
      }

      // 메시지 데이터 처리
      if (results[1]?.status === 'fulfilled') {
        const messageResult = results[1]?.value
        messageData.value = Array.isArray(messageResult) ? messageResult : []
      }
      else {
        messageData.value = []
      }

      // 캠페인 데이터 처리 (광고주만)
      if (isAdvertiser && results[2]) {
        if (results[2].status === 'fulfilled') {
          campaignData.value = (results[2] as PromiseFulfilledResult<any[]>).value || []
        }
        else {
          campaignData.value = []
        }
      }
      else {
        campaignData.value = []
      }
    }
    catch {
      // 오류 시 빈 배열로 초기화
      contractData.value = []
      messageData.value = []
      campaignData.value = []
    }
  }

  const unsubscribe = ref() // OAuth 콜백 파라미터 전역 변수 (모든 소셜 로그인 공통 사용)
  const oauthCallbackParams = ref({
    code: null as string | null,
    state: null as string | null,
    error: null as string | null,
    provider: null as string | null, // 어떤 소셜 로그인인지 식별
  })

  async function getUserList() {
    loading.value = true
    try {
      const q = query(collection(db, 'user'), orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      documents.value = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
    }
    catch (error) {
      console.error('Error fetching user list:', error)
    }
    finally {
      loading.value = false
    }
  }

  async function getCreatorList() {
    loading.value = true
    documents.value = []
    try {
      const q = query(collection(db, 'user'), where('type', '==', 'creator'), orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      documents.value = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
    }
    catch (error) {
      console.error('Error fetching user list:', error)
    }
    finally {
      loading.value = false
    }
  }
  async function getAdvertiserList() {
    loading.value = true
    documents.value = []
    try {
      const q = query(collection(db, 'user'), where('type', '==', 'advertiser'), orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      documents.value = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
    }
    catch (error) {
      console.error('Error fetching user list:', error)
    }
    finally {
      loading.value = false
    }
  }

  async function getUser(id: string) {
    loading.value = true
    try {
      const docRef = doc(db, 'user', id)
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        document.value = { id: docSnap.id, ...docSnap.data() }
        uploadDocument.value = { id: docSnap.id, ...docSnap.data() }
      }
      else {
        console.error('No such document!')
      }
    }
    catch (error) {
      console.error('Error fetching carousel:', error)
    }
    finally {
      loading.value = false
    }
  }

  // 사용자 정보 가져오기, 로그인 상태 확인 (atomray3d 방식)
  function getCurrentUser() {
    loading.value = true
    return new Promise((resolve, reject) => {
      unsubscribe.value = onAuthStateChanged(
        auth,
        async (user: any) => {
          if (user) {
            // 먼저 사용자 타입 확인
            const userType = await checkAndSetUserType(user.uid)

            if (userType) {
              // creator/advertiser 컬렉션에서 데이터 가져오기
              try {
                const userDocRef = doc(db, userType, user.uid)
                const userDoc = await getDoc(userDocRef)

                if (userDoc.exists()) {
                  userStoreData.value = {
                    ...userDoc.data(),
                    uid: user.uid,
                    type: userType,
                  }
                  getUserDataLoading.value = true
                  // 현재 페이지가 myHome이면 계약 데이터와 메시지 데이터도 로드
                  if (router) {
                    const currentRoute = router.currentRoute.value
                    // 더 정확한 라우트 체크
                    if (currentRoute.path.includes('myHome') || currentRoute.name?.includes('myHome')) {
                      await loadMyHomeData()
                    }
                  }
                }
                else {
                  // creator/advertiser 컬렉션에 문서가 없으면 기본 정보로 설정
                  userStoreData.value = {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                    type: userType,
                    accountType: user.providerData[0]?.providerId || 'email',
                  }
                  getUserDataLoading.value = true
                }
              }
              catch (error) {
                // Firestore 오류 시 기본 정보로 설정
                /* userStoreData.value = {
                  uid: user.uid,
                  email: user.email,
                  displayName: user.displayName,
                  photoURL: user.photoURL,
                  type: userType,
                  accountType: user.providerData[0]?.providerId || 'email',
                } */
                console.error('Error fetching user type document:', error)
                getUserDataLoading.value = true
              }
            }
          }
          else {
            userStoreData.value = null
            getUserDataLoading.value = true
          }
          loading.value = false
          getUserDataLoading.value = true
          isSessionInitialized.value = true
          resolve(user)
        },
        e => reject(e),
      )
    })
  }

  async function emailRegister(email: string, password: string) {
    loading.value = true
    try {
      // 이메일과 비밀번호 기본 검사
      if (!email?.trim() || !password) {
        throw new Error('이메일과 비밀번호를 확인해주세요.')
      }

      // Firebase email registration logic here
      const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password)
      // Signed in
      const user = userCredential.user

      // Firebase Auth만 사용, Firestore user 컬렉션에는 저장하지 않음
      // 필요한 정보는 메모리에만 보관하고 signUp에서 최종 저장
      userStoreData.value = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        accountType: 'email',
        createdAt: new Date(),
      }

      // Firebase Auth가 자동으로 세션 관리

      systemStore.showSuccess('회원가입이 완료되었습니다.')
      // router.push는 signUp.vue에서 처리하므로 제거
    }
    catch (error: any) {
      if (error.code) {
        systemStore.showError(error.message)
      }
    }
    finally {
      loading.value = false
    }
  }

  async function emailLogin(email: string, password: string, remember = true) {
    loading.value = true
    try {
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence)
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password)
      const user = userCredential.user

      // Firebase Auth user 정보를 userStoreData에 임시 설정
      userStoreData.value = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        accountType: 'email',
      }

      // Firestore에서 사용자의 상세 정보를 가져오기
      const userDocRef = doc(db, 'user', user.uid)
      const userDoc = await getDoc(userDocRef)

      if (userDoc.exists()) {
        // Firestore에 사용자 정보가 있으면 그것을 사용
        userStoreData.value = {
          ...userDoc.data(),
          uid: user.uid,
        }
      }
      else {
        console.warn('Firestore에 사용자 문서 없음')
      }

      // 실제 계정 타입을 creator/advertiser 컬렉션에서 확인
      await checkAndSetUserType(user.uid)

      // Firebase Auth가 자동으로 세션 관리
      if (userStoreData.value) {
        // 반응성 강제 업데이트를 위해 nextTick 사용
        await nextTick()
      }
    }
    catch (error: any) {
      userStoreData.value = null

      // Firebase Auth 에러 메시지를 한국어로 변환
      let errorMessage = '로그인 중 오류가 발생했습니다.'
      if (error.code === 'auth/user-not-found') {
        errorMessage = '존재하지 않는 사용자입니다.'
      }
      else if (error.code === 'auth/wrong-password') {
        errorMessage = '비밀번호가 올바르지 않습니다.'
      }
      else if (error.code === 'auth/invalid-credential') {
        errorMessage = '이메일 또는 비밀번호가 올바르지 않습니다.'
      }
      else if (error.code === 'auth/network-request-failed') {
        errorMessage = '네트워크 연결을 확인한 후 다시 로그인해주세요.'
      }
      else if (error.code === 'auth/invalid-email') {
        errorMessage = '유효하지 않은 이메일 형식입니다.'
      }
      else if (error.code === 'auth/user-disabled') {
        errorMessage = '비활성화된 계정입니다.'
      }
      else if (error.code === 'auth/too-many-requests') {
        errorMessage = '너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요.'
      }

      systemStore.showError(errorMessage)
      throw error // 호출하는 쪽에서 에러를 감지할 수 있도록
    }
    finally {
      loading.value = false
    }
  }

  // 사용자의 실제 계정 타입을 확인하고 설정하는 함수
  async function checkAndSetUserType(uid: string) {
    try {
      // creator 컬렉션에서 확인
      const creatorDocRef = doc(db, 'creator', uid)
      const creatorDoc = await getDoc(creatorDocRef)

      if (creatorDoc.exists()) {
        // creator 데이터가 있으면 type을 'creator'로 설정
        userStoreData.value = {
          ...userStoreData.value,
          type: 'creator',
          creatorData: creatorDoc.data(),
        }
        return 'creator'
      }

      // advertiser 컬렉션에서 확인
      const advertiserDocRef = doc(db, 'advertiser', uid)
      const advertiserDoc = await getDoc(advertiserDocRef)

      if (advertiserDoc.exists()) {
        // advertiser 데이터가 있으면 type을 'advertiser'로 설정
        userStoreData.value = {
          ...userStoreData.value,
          type: 'advertiser',
          advertiserData: advertiserDoc.data(),
        }
        return 'advertiser'
      }

      // 어느 컬렉션에도 없으면 타입이 설정되지 않은 상태
      return null
    }
    catch {
      return null
    }
  }

  // Firebase Custom Token으로 소셜 로그인 처리 (통합 함수)
  // 추천 형태
  async function signInWithCustomTokenForSocial(params: {
    provider: 'kakao' | 'naver' | 'apple'
    accessToken?: string // kakao, naver
    identityToken?: string // apple
  }) {
    let url = ''
    let body: any = {}

    if (params.provider === 'kakao') {
      url = 'https://us-central1-oo-ing.cloudfunctions.net/kakaoCustomToken'
      body = { accessToken: params.accessToken }
    }
    else if (params.provider === 'naver') {
      url = 'https://us-central1-oo-ing.cloudfunctions.net/naverCustomToken'
      body = { accessToken: params.accessToken }
    }
    else if (params.provider === 'apple') {
      url = 'https://us-central1-oo-ing.cloudfunctions.net/appleCustomToken'
      body = { identityToken: params.identityToken }
    }

    const tokenResponse = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })

    if (!tokenResponse.ok)
      throw new Error(`Custom Token 생성 실패: ${tokenResponse.status}`)

    const { customToken } = await tokenResponse.json()

    const userCredential = await signInWithCustomToken(auth, customToken)
    const user = userCredential.user

    userStoreData.value = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      accountType: params.provider,
    }

    const userType = await checkAndSetUserType(user.uid)

    return { user, userType }
  }

  /* async function socialLogin(userData: {
    uid: string
    email: string | null
    name: string | null
    photo: string | null
    provider: string
    nickname?: string
    mobile?: number
    profile_image?: string
  }) {
    loading.value = true
    try {
      console.warn('🔥 통합 소셜 로그인 프로세스 시작:', {
        uid: userData.uid,
        provider: userData.provider,
        email: userData.email,
      })

      // Firebase Custom Token을 통한 통합 인증 처리
      const { user, userType } = await signInWithCustomTokenForSocial(userData)

      if (!userType) {
        // 새 사용자 - 회원가입 미완료 상태로 임시 저장
        console.warn(' 신규 소셜 사용자 - 회원가입 페이지로 이동')

        // 회원가입 완료까지는 임시 데이터로만 저장
        tempSocialUserData.value = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          accountType: userData.provider,
          createdAt: new Date(),
        }

        // 카카오/Apple/네이버 사용자는 정보가 부족하므로 항상 register.vue로 이동
        if (userData.provider === 'kakao' || userData.provider === 'apple' || userData.provider === 'naver') {
          // 카카오/Apple/네이버 사용자는 register.vue에서 추가 정보를 입력할 수 있도록 일부 정보만 설정
          userStoreData.value = {
            uid: user.uid,
            email: user.email || '', // Apple, 네이버는 이메일이 있을 수도 있음
            displayName: user.displayName || '', // 네이버는 닉네임이 있을 수 있음
            photoURL: user.photoURL,
            accountType: userData.provider,
          }

          // 미완료 소셜 사용자도 세션에 저장 (회원가입 페이지 이동 시 데이터 유지) - Firebase Auth가 관리하므로 제거됨
          // saveUserSession(userStoreData.value)

          const providerName = userData.provider === 'kakao'
            ? '카카오'
            : userData.provider === 'apple' ? 'Apple' : '네이버'
          systemStore.showSuccess(`${providerName} 로그인이 완료되었습니다. 추가 정보를 입력해주세요.`)
          // 재로딩 방지를 위해 router.push 사용
          await router.push('/user/register')
        }
        else {
          userStoreData.value = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            accountType: userData.provider,
            createdAt: new Date(),
          }

          // 미완료 소셜 사용자도 세션에 저장 (계정 유형 선택 페이지 이동 시 데이터 유지) - Firebase Auth가 관리하므로 제거됨
          // saveUserSession(userStoreData.value)

          systemStore.showSuccess('소셜 로그인이 완료되었습니다. 계정 유형을 선택해주세요.')
          // 재로딩 방지를 위해 router.push 사용
          await router.push('/user/select')
        }
      }
      else {
        // 기존 사용자는 정식 로그인 상태로 설정
        console.warn('✅ 기존 소셜 사용자 로그인 완료:', userType)

        userStoreData.value = {
          ...userStoreData.value,
          type: userType,
        }
        systemStore.showSuccess('로그인이 완료되었습니다.')

        // Firebase Auth가 자동으로 세션을 관리하므로 localStorage 저장 불필요
        console.warn('💾 Firebase Auth 세션 자동 관리 활성화')

        // 사용자 타입에 따라 다른 페이지로 리다이렉트 (재로딩 방지)
        if (userType === 'creator') {
          console.warn('크리에이터 - 캠페인 탐색 페이지로 이동')
          await router.push('/creator/campaigns')
        }
        else if (userType === 'advertiser') {
          console.warn('광고주 - 캠페인 관리 페이지로 이동')
          await router.push('/advertiser/campaigns')
        }
        else {
          // 기본값으로 홈페이지 이동 (타입 미분류 사용자)
          console.warn('사용자 타입 미분류 - select로 이동')
          await router.push('/user/select')
        }
      }

      console.warn('✅ 통합 소셜 로그인 프로세스 완료')
    }
    catch (error: any) {
      console.error('통합 소셜 로그인 오류:', error)
      userStoreData.value = null
      tempSocialUserData.value = null
      systemStore.showError(error instanceof Error ? error.message : '소셜 로그인 중 오류가 발생했습니다.')
      throw error
    }
    finally {
      loading.value = false
    }
  } */

  async function googleLogin() {
    try {
      loading.value = true

      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      const user = result.user

      /* console.warn('✅ Google 로그인 성공:', {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
      }) */

      // 사용자 정보 설정
      userStoreData.value = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        accountType: 'google',
      }

      // 사용자 타입 확인
      const userType = await checkAndSetUserType(user.uid)

      if (!userType) {
        // 신규 사용자 - 회원가입 페이지로 이동
        systemStore.showSuccess('Google 로그인이 완료되었습니다. 추가 정보를 입력해주세요.')
        await router.push('/user/register')
      }
      else {
        // 기존 사용자 - 타입에 따라 페이지 이동
        systemStore.showSuccess('로그인이 완료되었습니다.')

        if (userType === 'creator') {
          await router.push('/creator/campaigns')
        }
        else if (userType === 'advertiser') {
          await router.push('/advertiser/campaigns')
        }
        else {
          await router.push('/user/select')
        }
      }
    }
    catch (error: any) {
      console.error('❌ Google 로그인 실패:', error)

      let errorMessage = 'Google 로그인 중 오류가 발생했습니다.'

      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = '로그인이 취소되었습니다.'
      }
      else if (error.code === 'auth/popup-blocked') {
        errorMessage = '팝업이 차단되었습니다. 팝업을 허용해주세요.'
      }
      else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = '로그인 요청이 취소되었습니다.'
      }

      systemStore.showError(errorMessage)
      throw error
    }
    finally {
      loading.value = false
    }
  }

  // Apple 로그인 - Firebase 표준 방식 (팝업)
  async function appleLogin() {
    try {
      loading.value = true

      // OAuthProvider 인스턴스 생성
      const provider = new OAuthProvider('apple.com')

      // 기본 범위 추가
      provider.addScope('email')
      provider.addScope('name')

      // 한국어 로케일 설정 (선택사항)
      provider.setCustomParameters({
        locale: 'ko',
      })

      // Firebase Auth 팝업을 통한 Apple 로그인
      const result = await signInWithPopup(auth, provider)
      const user = result.user

      // 사용자 정보 설정
      userStoreData.value = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        accountType: 'apple',
      }

      // 사용자 타입 확인
      const userType = await checkAndSetUserType(user.uid)

      if (!userType) {
        // 신규 사용자 - 회원가입 페이지로 이동
        systemStore.showSuccess('Apple 로그인이 완료되었습니다. 추가 정보를 입력해주세요.')
        await router.push('/user/register')
      }
      else {
        // 기존 사용자 - 타입에 따라 페이지 이동
        systemStore.showSuccess('로그인이 완료되었습니다.')

        if (userType === 'creator') {
          await router.push('/creator/campaigns')
        }
        else if (userType === 'advertiser') {
          await router.push('/advertiser/campaigns')
        }
        else {
          await router.push('/user/select')
        }
      }
    }
    catch (error: any) {
      console.error('❌ Apple 로그인 실패:', error)

      let errorMessage = 'Apple 로그인 중 오류가 발생했습니다.'

      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = '로그인 팝업이 닫혔습니다.'
      }
      else if (error.code === 'auth/popup-blocked') {
        errorMessage = '팝업이 차단되었습니다. 팝업 차단을 해제하고 다시 시도해주세요.'
      }
      else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = '다른 로그인이 진행 중입니다.'
      }
      else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = '다른 로그인 방법으로 등록된 계정입니다.'
      }

      systemStore.showError(errorMessage)
    }
    finally {
      loading.value = false
    }
  }

  // Apple 로그인 - 리다이렉트 방식 (모바일 친화적)
  async function appleLoginWithRedirect() {
    try {
      loading.value = true

      // OAuthProvider 인스턴스 생성
      const provider = new OAuthProvider('apple.com')

      // 기본 범위 추가
      provider.addScope('email')
      provider.addScope('name')

      // 한국어 로케일 설정
      provider.setCustomParameters({
        locale: 'ko',
      })

      // Firebase Auth 리다이렉트를 통한 Apple 로그인
      await signInWithRedirect(auth, provider)

      // 리다이렉트되므로 이 코드는 실행되지 않음
    }
    catch (error: any) {
      console.error('❌ Apple 리다이렉트 로그인 실패:', error)
      systemStore.showError('Apple 로그인 중 오류가 발생했습니다.')
      loading.value = false
    }
  }

  // Apple 로그인 리다이렉트 결과 처리
  async function handleAppleRedirectResult() {
    try {
      loading.value = true

      const result = await getRedirectResult(auth)

      if (result) {
        const user = result.user

        // 사용자 정보 설정
        userStoreData.value = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          accountType: 'apple',
        }

        // 사용자 타입 확인
        const userType = await checkAndSetUserType(user.uid)

        if (!userType) {
          // 신규 사용자 - 회원가입 페이지로 이동
          systemStore.showSuccess('Apple 로그인이 완료되었습니다. 추가 정보를 입력해주세요.')
          await router.push('/user/register')
        }
        else {
          // 기존 사용자 - 타입에 따라 페이지 이동
          systemStore.showSuccess('로그인이 완료되었습니다.')

          if (userType === 'creator') {
            await router.push('/creator/campaigns')
          }
          else if (userType === 'advertiser') {
            await router.push('/advertiser/campaigns')
          }
          else {
            await router.push('/user/select')
          }
        }
      }
    }
    catch (error: any) {
      console.error('❌ Apple 리다이렉트 결과 처리 실패:', error)

      let errorMessage = 'Apple 로그인 중 오류가 발생했습니다.'

      if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = '다른 로그인 방법으로 등록된 계정입니다.'
      }

      systemStore.showError(errorMessage)
    }
    finally {
      loading.value = false
    }
  }

  async function kakaoLogin() {
    try {
      loading.value = true

      // Kakao SDK 확인
      if (typeof window.Kakao === 'undefined') {
        throw new TypeError('카카오 SDK가 로드되지 않았습니다.')
      }

      const config = useRuntimeConfig()
      const kakaoAppKey = config.public.kakaoAppKey as string

      // 카카오 SDK 초기화
      if (!window.Kakao.isInitialized()) {
        if (kakaoAppKey === 'YOUR_KAKAO_APP_KEY') {
          throw new Error('카카오 앱 키가 설정되지 않았습니다. 2단계 인증 완료 후 설정해주세요.')
        }
        window.Kakao.init(kakaoAppKey)
      }

      // 환경에 따른 Redirect URI 설정 - 카카오 개발문서 참고
      const redirectUri = `${window.location.origin}/user/kakaoAuth`

      // 상태값 생성 및 저장
      const state = crypto.randomUUID()
      sessionStorage.setItem('kakao_state', state)

      // 카카오 로그인 (리다이렉트 방식) - 안정적인 설정
      window.Kakao.Auth.authorize({
        redirectUri,
        state,
        // 기본 정보만 요청하여 권한 문제 최소화
      })
    }
    catch (error) {
      systemStore.showError(error instanceof Error ? error.message : '카카오 로그인 초기화에 실패했습니다.')
      loading.value = false
    }
  }

  async function naverLogin() {
    try {
      loading.value = true

      const config = useRuntimeConfig()
      const naverClientId = config.public.naverClientId as string

      // 네이버 클라이언트 ID 확인
      if (!naverClientId || naverClientId === 'YOUR_NAVER_CLIENT_ID') {
        throw new Error('네이버 클라이언트 ID가 설정되지 않았습니다. .env 파일을 확인해주세요.')
      }

      // 환경에 따른 Redirect URI 설정
      const redirectUri = `${window.location.origin}/user/naverAuth`

      // 상태값 생성 및 저장 (CSRF 방지)
      const state = crypto.randomUUID()
      sessionStorage.setItem('naver_state', state)

      // 네이버 OAuth 인증 URL 생성
      const authUrl = new URL('https://nid.naver.com/oauth2.0/authorize')
      authUrl.searchParams.set('response_type', 'code')
      authUrl.searchParams.set('client_id', naverClientId)
      authUrl.searchParams.set('redirect_uri', redirectUri)
      authUrl.searchParams.set('state', state)

      // 네이버 로그인 페이지로 리다이렉트
      window.location.href = authUrl.toString()
    }
    catch (error) {
      systemStore.showError(error instanceof Error ? error.message : '네이버 로그인 초기화에 실패했습니다.')
      loading.value = false
    }
  }

  // 소셜 로그인 콜백 처리 중복 방지용 플래그들
  let isHandlingKakaoCallback = false
  let isHandlingNaverCallback = false

  async function logout(silent: boolean = false) {
    loading.value = true
    try {
      // Firebase Auth에서 로그아웃 (세션 자동 정리)
      await signOut(auth)

      // 데이터 정리 추가
      contractData.value = []
      messageData.value = []
      userStoreData.value = null
      tempSocialUserData.value = null

      // unsubscribe 처리
      if (unsubscribe.value) {
        unsubscribe.value()
        unsubscribe.value = null
        loading.value = false
      }
      /* console.warn('✅ Firebase Auth 로그아웃 완료') */

      // silent가 true가 아닐 때만 성공 메시지 처리
      if (!silent) {
        systemStore.showSuccess('로그아웃 되었습니다.')
        router.push('/')
      }
      loading.value = false
    }
    catch (error) {
      console.error('Firebase Auth 로그아웃 오류:', error)
      if (!silent) {
        systemStore.showError('로그아웃 중 오류가 발생했습니다.')
      }
      loading.value = false
    }
  }

  // 카카오 사용자 처리 (비밀번호 없는 일반 사용자로 처리)
  /* async function handleKakaoUserLogin(kakaoUserInfo: any) {
    try {
      console.warn('카카오 사용자 로그인 처리 시작:', {
        id: kakaoUserInfo.id,
        timestamp: kakaoUserInfo.timestamp,
      })

      // socialLogin 함수를 사용하여 Google 로그인과 동일한 패턴으로 처리
      // 주의: 이 함수는 API 호출 실패시 임시로만 사용되며, 실제 카카오 ID가 아닌 코드 기반 ID 사용
      await socialLogin({
        uid: `kakao:temp_${kakaoUserInfo.id}`, // API 실패시 임시 UID
        email: '', // 카카오에서는 이메일 정보 없음 - register.vue에서 입력받음
        name: '', // 이름도 register.vue에서 입력받음
        photo: '',
        provider: 'kakao',
        nickname: '', // 닉네임도 register.vue에서 입력받음
      })
    }
    catch (error) {
      console.error('카카오 사용자 처리 중 오류:', error)
      throw error
    }
  } */

  // 카카오 로그인 콜백 처리 (카카오 개발문서 참고)
  async function handleKakaoCallback() {
  // 중복 실행 방지
    if (isHandlingKakaoCallback) {
      return
    }

    isHandlingKakaoCallback = true

    try {
      loading.value = true

      // 전역 변수에 null로 먼저 초기화
      oauthCallbackParams.value = {
        code: null,
        state: null,
        error: null,
        provider: 'kakao',
      }

      // URL 파라미터 파싱
      const urlParams = new URLSearchParams(window.location.search)
      oauthCallbackParams.value.code = urlParams.get('code')
      oauthCallbackParams.value.state = urlParams.get('state')
      oauthCallbackParams.value.error = urlParams.get('error')

      /* console.warn('카카오 URLSearchParams 결과:')
      console.warn('- code:', oauthCallbackParams.value.code)
      console.warn('- state:', oauthCallbackParams.value.state)
      console.warn('- error:', oauthCallbackParams.value.error)
      console.warn('- 전체 파라미터:', Array.from(urlParams.entries())) */

      // 저장된 state 확인
      const storedState = sessionStorage.getItem('kakao_state')
      /* console.warn('저장된 kakao_state:', storedState ? `${storedState.substring(0, 8)}***` : 'null')
      console.warn('================================') */

      // URL 파라미터를 즉시 정리 (다른 페이지로 이동 시 중복 처리 방지)
      window.history.replaceState({}, '', window.location.pathname)

      // 오류 확인
      if (oauthCallbackParams.value.error) {
        throw new Error(`카카오 로그인 오류: ${oauthCallbackParams.value.error}`)
      }

      // 파라미터가 모두 없는 경우 - 기존 사용자일 가능성
      if (!oauthCallbackParams.value.code && !oauthCallbackParams.value.state && !oauthCallbackParams.value.error) {
        /* console.warn('카카오 OAuth 파라미터 없음 - 기존 로그인 사용자일 가능성 확인 시작') */

        // 1차: Kakao SDK에서 액세스 토큰 확인
        if (typeof window.Kakao !== 'undefined' && window.Kakao.Auth) {
          const accessToken = window.Kakao.Auth.getAccessToken()
          /* console.warn('카카오 액세스 토큰:', accessToken ? '존재함' : '없음') */

          if (accessToken) {
            await signInWithCustomTokenForSocial({
              provider: 'kakao',
              accessToken,
            })

            /* console.warn('✅ 카카오 로그인 콜백 처리 완료 (액세스 토큰 방법)') */
            return
          }
        }

        // 2차: sessionStorage에서 저장된 토큰 확인
        const kakaoLoginData = sessionStorage.getItem('kakao_login_data')
        if (kakaoLoginData) {
          try {
            const loginData = JSON.parse(kakaoLoginData)

            if (loginData.access_token) {
              await signInWithCustomTokenForSocial({
                provider: 'kakao',
                accessToken: loginData.access_token,
              })

              /* console.warn('✅ 카카오 로그인 콜백 처리 완료 (저장된 토큰 방법)') */
              return
            }
          }
          catch (e) {
            console.error('sessionStorage 데이터 파싱 오류:', e)
          }
        }

        // 여기까지 왔는데도 토큰이 없으면 에러
        throw new Error('카카오 인증 정보를 찾을 수 없습니다. 다시 로그인해주세요.')
      }

      // 인증 코드 확인
      if (!oauthCallbackParams.value.code) {
        console.error('인증 코드 없음')
        throw new Error('인증 코드를 찾을 수 없습니다.')
      }

      // 저장된 state와 비교 (보안 확인)
      if (!storedState || storedState !== oauthCallbackParams.value.state) {
        console.error('state 불일치:', { stored: storedState, received: oauthCallbackParams.value.state })
        throw new Error('보안 검증에 실패했습니다.')
      }

      // 세션 스토리지 정리
      sessionStorage.removeItem('kakao_state')

      /* console.warn('✅ 카카오 OAuth 콜백 처리 성공:', {
        code: !!oauthCallbackParams.value.code,
        state: `${oauthCallbackParams.value.state?.substring(0, 8)}***`,
        hostname: window.location.hostname,
      }) */

      // 카카오 인증 코드로 액세스 토큰을 얻고 실제 카카오 사용자 정보 조회
      /* console.warn('🔥 카카오 인증 코드로 사용자 정보 조회 시작') */

      // 환경에 따른 redirect URI 설정 (로그인할 때와 동일해야 함)
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
      const redirectUri = `${window.location.origin}/user/kakaoAuth`

      try {
        let tokenData: any

        if (isProduction) {
        // 프로덕션: 카카오 API 직접 호출
          const config = useRuntimeConfig()
          const clientId = config.public.kakaoAppKey

          const tokenParams = new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: clientId,
            redirect_uri: redirectUri,
            code: oauthCallbackParams.value.code!,
          })

          const tokenResponse = await fetch('https://kauth.kakao.com/oauth/token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
            },
            body: tokenParams.toString(),
          })

          if (!tokenResponse.ok) {
            throw new Error('카카오 토큰 교환 실패')
          }

          tokenData = await tokenResponse.json()
        }
        else {
        // 개발환경: 서버 API 사용
          const tokenResponse = await fetch(`/api/kakao/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code: oauthCallbackParams.value.code,
              redirectUri,
            }),
          })

          if (!tokenResponse.ok) {
            throw new Error('카카오 토큰 교환 실패')
          }

          tokenData = await tokenResponse.json()
        }

        if (!tokenData.access_token) {
          throw new Error('카카오 액세스 토큰을 받지 못했습니다')
        }

        /*  console.warn('✅ 카카오 토큰 교환 성공') */

        // (선택) 액세스 토큰으로 카카오 사용자 정보 조회 - 디버깅/검증용
        const userInfoResponse = await fetch('https://kapi.kakao.com/v2/user/me', {
          headers: {
            'Authorization': `Bearer ${tokenData.access_token}`,
            'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
          },
        })

        if (!userInfoResponse.ok) {
          throw new Error('카카오 사용자 정보 조회 실패')
        }

        // Firebase Custom Token으로 최종 로그인 처리
        await signInWithCustomTokenForSocial({
          provider: 'kakao',
          accessToken: tokenData.access_token,
        })

        /* console.warn('✅ 카카오 로그인 콜백 처리 완료 (실제 사용자 ID 방식)') */
      }
      catch (apiError) {
        console.error('카카오 API 호출 실패:', apiError)
        // 예전 fallback(handleKakaoUserLogin) 없이, 그냥 상위 catch로 넘김
        throw apiError
      }
    }
    catch (error) {
      console.error('❌ 카카오 로그인 처리 실패:', error)
      systemStore.showError(error instanceof Error ? error.message : '카카오 로그인 중 오류가 발생했습니다.')
      throw error
    }
    finally {
      loading.value = false
      isHandlingKakaoCallback = false
      /* console.warn('🔓 카카오 콜백 처리 완료 - 전역 플래그 해제') */
    }
  }

  // 네이버 로그인 콜백 처리
  async function handleNaverCallback() {
    // 중복 실행 방지
    if (isHandlingNaverCallback) {
      /* console.warn('🛑 네이버 콜백 중복 실행 방지 - 이미 처리 중입니다.') */
      return
    }

    isHandlingNaverCallback = true
    /* console.warn('🔒 네이버 콜백 처리 시작 - 전역 플래그 설정') */

    try {
      loading.value = true

      // 전역 변수에 null로 먼저 초기화
      oauthCallbackParams.value = {
        code: null,
        state: null,
        error: null,
        provider: 'naver',
      }

      // URL 파라미터 파싱
      const urlParams = new URLSearchParams(window.location.search)
      oauthCallbackParams.value.code = urlParams.get('code')
      oauthCallbackParams.value.state = urlParams.get('state')
      oauthCallbackParams.value.error = urlParams.get('error')

      /* console.warn('네이버 URLSearchParams 결과:', {
        code: !!oauthCallbackParams.value.code,
        state: oauthCallbackParams.value.state ? `${oauthCallbackParams.value.state.substring(0, 8)}***` : 'null',
        error: oauthCallbackParams.value.error,
      }) */

      // 저장된 state 확인
      const storedState = sessionStorage.getItem('naver_state')
      /* console.warn('저장된 naver_state:', storedState ? `${storedState.substring(0, 8)}***` : 'null') */

      // URL 파라미터를 즉시 정리 (다른 페이지로 이동 시 중복 처리 방지)
      window.history.replaceState({}, '', window.location.pathname)

      // 오류 확인
      if (oauthCallbackParams.value.error) {
        console.error('네이버 로그인 에러:', oauthCallbackParams.value.error)
        throw new Error(`네이버 로그인 오류: ${oauthCallbackParams.value.error}`)
      }

      // 인증 코드 확인
      if (!oauthCallbackParams.value.code) {
        console.error('인증 코드 없음')
        throw new Error('네이버 인증 코드를 찾을 수 없습니다.')
      }

      // 저장된 state와 비교 (보안 확인)
      if (!storedState || storedState !== oauthCallbackParams.value.state) {
        console.error('state 불일치:', { stored: storedState, received: oauthCallbackParams.value.state })
        throw new Error('보안 검증에 실패했습니다.')
      }

      // 세션 스토리지 정리
      sessionStorage.removeItem('naver_state')

      /* console.warn('✅ 네이버 OAuth 콜백 처리 성공:', {
        code: !!oauthCallbackParams.value.code,
        state: `${oauthCallbackParams.value.state?.substring(0, 8)}***`,
        hostname: window.location.hostname,
      }) */

      // 네이버 인증 코드로 액세스 토큰을 얻고 사용자 정보 조회
      /* console.warn('🔥 네이버 인증 코드로 사용자 정보 조회 시작') */

      // 환경에 따른 redirect URI 설정 (로그인할 때와 동일해야 함)
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
      const redirectUri = `${window.location.origin}/user/naverAuth`

      try {
        // 1. 네이버 토큰 교환 (프로덕션과 개발환경 구분)
        let tokenData

        if (isProduction) {
          // 프로덕션: 네이버 API 직접 호출
          // 프로덕션: Firebase Function을 통한 네이버 토큰 교환
          const tokenResponse = await fetch(
            'https://us-central1-oo-ing.cloudfunctions.net/naverToken',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                code: oauthCallbackParams.value.code,
                state: oauthCallbackParams.value.state,
              }),
            },
          )

          /* console.warn('🔄 토큰 응답 상태:', {
            ok: tokenResponse.ok,
            status: tokenResponse.status,
            statusText: tokenResponse.statusText,
            headers: tokenResponse.headers,
          }) */

          if (!tokenResponse.ok) {
            const errorText = await tokenResponse.text()
            console.error('❌ 토큰 교환 실패 응답:', errorText)
            throw new Error(`네이버 토큰 교환 실패: ${tokenResponse.status} ${tokenResponse.statusText}`)
          }

          tokenData = await tokenResponse.json()
          /* console.warn('📦 받은 토큰 데이터:', tokenData) */
        }
        else {
          // 개발환경: 서버 API 사용
          const tokenResponse = await fetch(`/api/naver/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code: oauthCallbackParams.value.code,
              state: oauthCallbackParams.value.state,
              redirectUri,
            }),
          })

          if (!tokenResponse.ok) {
            throw new Error('네이버 토큰 교환 실패')
          }

          tokenData = await tokenResponse.json()
        }

        if (!tokenData.access_token) {
          console.error('❌ 토큰 데이터 상세:', {
            tokenData,
            hasAccessToken: !!tokenData?.access_token,
            keys: tokenData ? Object.keys(tokenData) : 'tokenData is null/undefined',
            error: tokenData?.error,
            errorDescription: tokenData?.error_description,
          })
          throw new Error(`네이버 액세스 토큰을 받지 못했습니다. 응답: ${JSON.stringify(tokenData)}`)
        }

        // 2. 네이버 사용자 정보 조회 (환경별 분리)
        let naverUserData

        if (isProduction) {
          // 프로덕션: Firebase Function 직접 호출
          const userInfoResponse = await fetch('https://naveruser-u6lyuu23cq-uc.a.run.app', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken: tokenData.access_token,
            }),
          })

          if (!userInfoResponse.ok) {
            const errorText = await userInfoResponse.text()
            console.error('❌ 네이버 사용자 정보 조회 실패 응답:', errorText)
            throw new Error(`네이버 사용자 정보 조회 실패: ${userInfoResponse.status}`)
          }

          naverUserData = await userInfoResponse.json()
        }
        else {
          // 개발환경: 서버 API 사용
          const userInfoResponse = await fetch('/api/naver/user', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken: tokenData.access_token,
            }),
          })

          if (!userInfoResponse.ok) {
            throw new Error('네이버 사용자 정보 조회 실패')
          }

          naverUserData = await userInfoResponse.json()
        }

        if (naverUserData.resultcode !== '00' || !naverUserData.response) {
          throw new Error('네이버 사용자 정보를 가져올 수 없습니다')
        }

        // const userProfile = naverUserData.response

        // 3. 네이버 사용자 ID로 socialLogin 처리
        await signInWithCustomTokenForSocial({
          provider: 'naver',
          accessToken: tokenData.access_token,
        })
      }
      catch (apiError) {
        console.error('네이버 API 호출 실패:', apiError)

        // API 실패시 임시 방식으로 대체 (코드 기반 ID는 임시용으로만)
        throw apiError
      }
    }
    catch (error) {
      console.error('❌ 네이버 로그인 처리 실패:', error)
      systemStore.showError(error instanceof Error ? error.message : '네이버 로그인 중 오류가 발생했습니다.')
      throw error
    }
    finally {
      loading.value = false
      isHandlingNaverCallback = false
    }
  }

  // 네이버 사용자 처리 (API 실패시 사용)
  /* async function handleNaverUserLogin(naverUserInfo: any) {
    try {
      console.warn('네이버 사용자 로그인 처리 시작:', {
        id: naverUserInfo.id,
        timestamp: naverUserInfo.timestamp,
      })

      // socialLogin 함수를 사용하여 다른 소셜 로그인과 동일한 패턴으로 처리
      await signInWithCustomTokenForSocial({
        provider: 'naver',
        accessToken: tokenData.access_token,
      })
    }
    catch (error) {
      console.error('네이버 사용자 처리 중 오류:', error)
      throw error
    }
  } */

  // 아이디 찾기 - 이름과 전화번호로 사용자 이메일 찾기
  async function findUserByNameAndPhone(name: string, phone: string) {
    try {
      loading.value = true

      // creator 컬렉션에서 검색
      const creatorQuery = query(
        collection(db, 'creator'),
        where('name', '==', name),
        where('phone', '==', phone),
      )
      const creatorSnapshot = await getDocs(creatorQuery)

      if (!creatorSnapshot.empty) {
        const userData = creatorSnapshot.docs[0]?.data()
        return userData?.email || null
      }

      // advertiser 컬렉션에서 검색
      const advertiserQuery = query(
        collection(db, 'advertiser'),
        where('name', '==', name),
        where('phone', '==', phone),
      )
      const advertiserSnapshot = await getDocs(advertiserQuery)

      if (!advertiserSnapshot.empty) {
        const userData = advertiserSnapshot.docs[0]?.data()
        return userData?.email || null
      }

      // user 컬렉션에서도 검색 (기존 데이터 호환성)
      const userQuery = query(
        collection(db, 'user'),
        where('name', '==', name),
        where('phone', '==', phone),
      )
      const userSnapshot = await getDocs(userQuery)

      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0]?.data()
        return userData?.email || null
      }

      return null
    }
    catch (error) {
      console.error('아이디 찾기 오류:', error)
      throw error
    }
    finally {
      loading.value = false
    }
  }

  // 비밀번호 재설정 이메일 전송
  async function sendPasswordResetEmailToUser(email: string) {
    try {
      loading.value = true

      // 먼저 해당 이메일이 등록된 사용자인지 확인
      const isRegisteredUser = await checkIfEmailExists(email)
      if (!isRegisteredUser) {
        throw new Error('등록되지 않은 이메일입니다.')
      }

      // Firebase Auth를 통해 비밀번호 재설정 이메일 전송
      await sendPasswordResetEmail(auth, email)
    }
    catch (error: any) {
      console.error('비밀번호 재설정 이메일 전송 오류:', error)
      throw error
    }
    finally {
      loading.value = false
    }
  }

  // 이메일이 등록된 사용자인지 확인
  async function checkIfEmailExists(email: string) {
    try {
      // creator 컬렉션에서 확인
      const creatorQuery = query(
        collection(db, 'creator'),
        where('email', '==', email),
      )
      const creatorSnapshot = await getDocs(creatorQuery)

      if (!creatorSnapshot.empty) {
        return true
      }

      // advertiser 컬렉션에서 확인
      const advertiserQuery = query(
        collection(db, 'advertiser'),
        where('email', '==', email),
      )
      const advertiserSnapshot = await getDocs(advertiserQuery)

      if (!advertiserSnapshot.empty) {
        return true
      }

      // user 컬렉션에서도 확인 (기존 데이터 호환성)
      const userQuery = query(
        collection(db, 'user'),
        where('email', '==', email),
      )
      const userSnapshot = await getDocs(userQuery)

      return !userSnapshot.empty
    }
    catch (error) {
      console.error('이메일 확인 오류:', error)
      return false
    }
  }

  const userDetail = ref<any>(null)

  async function loadUserDetail() {
    if (!userStoreData.value?.uid)
      return

    const uid = userStoreData.value.uid
    const type = userStoreData.value.type
    try {
      if (type === 'advertiser') {
        const advertiserDocRef = doc(db, 'advertiser', uid)
        const advertiserDoc = await getDoc(advertiserDocRef)
        if (advertiserDoc.exists()) {
          const data = advertiserDoc.data()
          userDetail.value = data
        }
      }
      else if (type === 'creator') {
        const creatorDocRef = doc(db, 'creator', uid)
        const creatorDoc = await getDoc(creatorDocRef)
        if (creatorDoc.exists()) {
          const data = creatorDoc.data()
          userDetail.value = data
        }
      }
    }
    catch (error) {
      console.error('사용자 상세 정보 로드 실패:', error)
    }
  }

  return {
    userStoreData,
    tempSocialUserData,
    documents,
    document,
    uploadDocument,
    contractData,
    messageData,
    campaignData,
    loading,
    isSessionInitialized,
    oauthCallbackParams,
    userDetail,
    getUserDataLoading,
    emailRegister,
    emailLogin,
    googleLogin,
    kakaoLogin,
    handleKakaoCallback,
    naverLogin,
    handleNaverCallback,
    getUserList,
    getUser,
    appleLogin,
    appleLoginWithRedirect,
    handleAppleRedirectResult,
    getCurrentUser,
    logout,
    getCreatorList,
    getAdvertiserList,
    checkAndSetUserType,
    loadMyHomeData,
    findUserByNameAndPhone,
    sendPasswordResetEmail: sendPasswordResetEmailToUser,
    checkIfEmailExists,
    loadUserDetail,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
